import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card } from "../../../../components/ui/Card";
import { showToast } from "../../../../utils/showToast";

/**
 * Página a la que vuelve el navegador cuando el backend terminó el OAuth de
 * TiendaNube. El backend agrega ?status=success|error y, según el caso,
 * ?alias=nombreTienda o ?error=code. Mostramos el resultado y devolvemos al
 * usuario a /home/integrations para que vea la tienda ya conectada.
 *
 * Espejo de JumpsellerCallback: ambos flujos OAuth son idénticos.
 */
export default function TiendaNubeCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const resolvedStatus = params.get("status") || "processing";
    const alias = params.get("alias");
    const errorCode = params.get("error");

    setStatus(resolvedStatus);

    if (resolvedStatus === "success") {
      showToast(
        alias
          ? t('Tienda TiendaNube "{{alias}}" vinculada correctamente', { alias })
          : t("Tienda TiendaNube vinculada correctamente"),
        true,
      );
    } else if (resolvedStatus === "error") {
      showToast(
        errorCode
          ? t("No se pudo vincular TiendaNube ({{error}})", { error: errorCode })
          : t("No se pudo vincular TiendaNube"),
        false,
      );
    }

    const timer = setTimeout(() => {
      navigate("/home/integrations", { replace: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [navigate, t]);

  return (
    <div className="flex justify-center items-center min-h-[60vh] px-4">
      <Card className="w-full max-w-[420px] text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-brand-500 animate-spin" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Procesando vinculación con TiendaNube…")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-status-success" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Tienda TiendaNube vinculada. Redirigiendo…")}</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-status-error" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("No se pudo completar la vinculación. Redirigiendo…")}</p>
          </>
        )}
      </Card>
    </div>
  );
}
